import React, { useContext, useState, useEffect } from 'react';

// Module imports.
import axios from 'axios';
import OTPInput from 'otp-input-react';
import codes from 'country-calling-code';
import { jwtDecode } from 'jwt-decode';

// Components import.
import { AuthContext, Animate, Button, Row, Card, ViewContext, useNavigate } from 'components/lib';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';

export function SigninMobileOTP(props) {
    const authContext = useContext(AuthContext);
    const viewContext = useContext(ViewContext);
    const navigate = useNavigate();

    const [phoneInput, setPhoneInput] = useState(false);
    const [OTP, setOTP] = useState('');
    const [countryCode, setCountryCode] = useState('91');
    const [loading, setLoading] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [enableVerify, setEnableVerify] = useState(false);

    const initialEditTime = 10;
    const initialResendTime = 15;
    const expirationTime = 15 * 60 * 1000; // 15 minutes

    const [resendTime, setResendTime] = useState(15);
    const [editTimer, setEditTimer] = useState(initialEditTime);
    const [resendTimer, setResendTimer] = useState(resendTime);
    const [editDisabled, setEditDisabled] = useState(true);
    const [resendDisabled, setResendDisabled] = useState(true);
    const [resendAttempts, setResendAttempts] = useState(1);
    const [expired, setExpired] = useState(false);
    const [timerStarted, setTimerStarted] = useState(false);


    // Css styles
    const paraContainer = {
        display: 'flex',
        justifyContent: 'center',
        textAlign: 'center',
        color: 'grey',
    };

    const optInput = {
        border: '1.5px solid #E2E8F0',
        marginRight: '5px',
        height: '3.5rem',
        width: '3.5rem',
        borderRadius: '10px',
        textAlign: 'center',
        color: 'black',
    };

    const numInput = {
        border: '1.5px solid #E2E8F0',
        marginRight: '5px',
        height: '3rem',
        width: '70%',
        borderRadius: '8px',
        color: 'black',
        paddingLeft: '10px',
    };

    const buttonContainer = {
        width: "100%",
        height: "3rem",
        fontSize: "1.2em",
        padding: "0.5em",
        marginBottom: "0.6em",
        fontWeight: "600"
    }

    // UseEffects
    useEffect(() => {
        if (!timerStarted) return;

        const editInterval = setInterval(() => {
            if (editTimer > 0) {
                setEditTimer(editTimer - 1);
            } else {
                setEditDisabled(false);
                clearInterval(editInterval);
            }
        }, 1000);

        return () => clearInterval(editInterval);
    }, [editTimer, timerStarted]);

    useEffect(() => {
        if (!timerStarted || !resendDisabled) return;

        const resendInterval = setInterval(() => {
            if (resendTimer > 0) {
                setResendTimer(resendTimer - 1);
            } else {
                setResendDisabled(false);
            }
        }, 1000);

        return () => clearInterval(resendInterval);
    }, [resendTimer, resendDisabled, timerStarted]);

    useEffect(() => {
        if (!timerStarted) return;

        const expirationTimeout = setTimeout(() => {
            setExpired(true);
            setResendDisabled(true);
        }, expirationTime);

        return () => clearTimeout(expirationTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timerStarted]);

    useEffect(() => {
        if (OTP.length === 6) {
            setEnableVerify(true);
        } else {
            setEnableVerify(false);
        }
    }, [OTP]);

    //Redirecting back to navigate if user is already signed in
    useEffect(() => {
        if (authContext.user?.token) {
            try {
                const decodedToken = jwtDecode(authContext.user.token);
                const expirationTime = decodedToken.exp * 1000;
                const currentTime = Date.now();

                if (currentTime < expirationTime) {
                    navigate("/navigate")
                }

            } catch {

            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authContext.user?.token])

    const validatePhone = (phoneNumber) => {
        const mobileNumberRegex = /^\s*(\d\s*){7,15}\s*$/;
        const isValid = mobileNumberRegex.test(phoneNumber);
        return isValid;
    };


    //Handling clicks

    const continueClick = (e) => {
        if (loading) return;
        setLoading(true);
        const validPhone = validatePhone(phoneNumber);
        if (!validPhone) {
            viewContext.notification.show(props.t('auth.signin.signinMobileOTP.error.invalidNumber'), 'error');
            setLoading(false);
            return;
        }

        const options = {
            method: 'POST',
            url: '/api/auth/mobile',
            data: {
                phone: phoneNumber,
                phoneNumber: countryCode + phoneNumber,
            },
        };

        axios
            .request(options)
            .then(function (response) {
                if (response.data.send === true) {
                    setPhoneInput(true);
                    setTimerStarted(false); // Stop existing timers
                    setEditTimer(initialEditTime);
                    setResendTimer(initialResendTime);
                    setEditDisabled(true);
                    setResendDisabled(true);
                    setExpired(false);
                    setResendAttempts(1);
                    setTimerStarted(true); // Start new timer

                    viewContext.notification.show(response.data.message, 'success');
                } else {
                    viewContext.notification.show(response.data.message, 'error');
                }
            })
            .catch((err) => {
                if (err.response?.data?.message) {
                    viewContext.notification.show(err.response?.data?.message, 'error');
                } else {
                    viewContext.notification.show(err.response.data, 'error');
                }
            });
        setLoading(false);
    };

    const verifyClick = () => {
        if (loading) return;
        setLoading(true);

        const options = {
            method: 'POST',
            url: '/api/auth/mobile/verify',
            data: {
                to: countryCode + phoneNumber,
                phoneNumber: phoneNumber,
                countryCode: countryCode,
                otp: OTP,
            },
        };

        axios
            .request(options)
            .then(function (response) {
                if (response.data) {
                    (response.data['2fa_required'] ? navigate(`/signin/otp?token=${response.data.token}`) : authContext.signin(response))
                } else {
                    viewContext.notification.show(response.data.message, 'error');
                }
            })
            .catch((err) => {
                if (err.response?.data?.message) {
                    viewContext.notification.show(err.response.data?.message, 'error');
                } else {
                    viewContext.notification.show(err.response.data, 'error');
                }
            });
        setLoading(false);
    };

    const handleResend = () => {
        const options = {
            method: 'POST',
            url: '/api/auth/mobile/resend',
            data: {
                to: countryCode + phoneNumber,
                phoneNumber: phoneNumber,
                countryCode: countryCode
            }
        };

        axios
            .request(options)
            .then(function (response) {
                if (response.data.send === true) {
                    setResendAttempts(resendAttempts + 1);
                    setResendTime(resendTime * 2);
                    setResendTimer(resendTime * 2);
                    setResendDisabled(true);

                    viewContext.notification.show(response.data.message, 'success');
                }
                else {
                    viewContext.notification.show(response.data.message, 'error');
                }
            })
            .catch((err) => {
                if (err.response?.data?.message) {
                    viewContext.notification.show(err.response.data?.message, 'error');
                } else {
                    viewContext.notification.show(err.response.data, 'error');
                }
            });
    };

    return (
        <Animate type='pop'>
            {!phoneInput && (
                <Row title={props.t('auth.signin.signinMobileOTP.mobileNumber.title')}>
                    <Button
                        text={props.t('auth.signin.signinMobileOTP.mobileNumber.usePassword')}
                        color='blue'
                        icon='shield'
                        iconColor='white'
                        className='mx-auto mb-5'
                        rounded iconButton small
                        action={() => { navigate('/signin') }}
                    />
                    <div style={paraContainer}>
                        <p>{props.t('auth.signin.signinMobileOTP.mobileNumber.info')}</p>
                    </div>
                    <Card loading={false} restrictWidth center>
                        <div style={{ display: 'flex' }}>
                            <select
                                style={{ ...numInput, width: '25%', paddingLeft: '3px' }}
                                onChange={(e) => {
                                    setCountryCode(e.target.value);
                                }}
                                value={countryCode}
                            >
                                {codes &&
                                    codes.map((country, index) => {
                                        return (
                                            <option key={country + index} value={country.countryCodes}>
                                                {country.isoCode3} +{country.countryCodes}
                                            </option>
                                        );
                                    })}
                            </select>

                            <input
                                type="text"
                                value={phoneNumber}
                                onChange={(e) => {
                                    setPhoneNumber(e.target.value);
                                }}
                                style={numInput}
                            />
                        </div>
                        <div style={{ ...paraContainer, marginTop: '20px' }}>
                            <AivagamButton
                                id='continueButton'
                                buttonText={props.t('auth.signin.signinMobileOTP.mobileNumber.button.continue')}
                                onClick={continueClick}
                                classStyles={buttonContainer}
                                type="confirm"
                                loading={loading}
                            />
                        </div>
                    </Card>
                </Row>
            )}
            {phoneInput && (
                <Row title={props.t('auth.signin.signinMobileOTP.otp.title')}>
                    <Button
                        text={props.t('auth.signin.signinMobileOTP.mobileNumber.usePassword')}
                        color='blue'
                        icon='shield'
                        iconColor='white'
                        className='mx-auto mb-5'
                        rounded iconButton small
                        action={() => { navigate('/signin') }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '80px' }}>
                        <p> {props.t('auth.signin.signinMobileOTP.otp.info')} {phoneNumber} </p>{' '}
                        <button
                            style={{ color: editDisabled ? 'grey' : '#3B82F6' }}
                            onClick={() => {
                                setPhoneInput(false);
                                setLoading(false);
                            }}
                            disabled={editDisabled}
                        >
                            {props.t('auth.signin.signinMobileOTP.otp.button.edit')} {editTimer > 0 && `${editTimer}s`}
                        </button>

                    </div>
                    <Card loading={false} restrictWidth center>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <OTPInput
                                value={OTP}
                                onChange={setOTP}
                                autoFocus
                                OTPLength={6}
                                otpType="number"
                                disabled={false}
                                inputStyles={optInput}
                            />
                        </div>
                        <div>
                            <p>	</p>
                        </div>
                        <div style={{ ...paraContainer, marginTop: '20px' }}>

                            <AivagamButton
                                id='continueButton'
                                buttonText={props.t('auth.signin.signinMobileOTP.otp.button.verify')}
                                onClick={verifyClick}
                                classStyles={buttonContainer}
                                type="confirm"
                                disabled={!enableVerify}
                                loading={loading}
                            />
                        </div>
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.4em",
                            alignItems: "flex-start"
                        }}>
                            {expired ?
                                <>
                                    <p> {props.t('auth.signin.signinMobileOTP.otp.new.info')} {
                                        <button
                                            style={{ color: '#3B82F6' }}
                                            onClick={continueClick}
                                        >
                                            {props.t('auth.signin.signinMobileOTP.otp.new.button')}	</button>}.</p>
                                </>
                                :
                                <button
                                    style={{ color: resendDisabled ? 'grey' : '#3B82F6' }}
                                    onClick={handleResend}
                                    disabled={resendDisabled}
                                >
                                    {props.t('auth.signin.signinMobileOTP.otp.button.resend')} {resendTimer > 0 && `${resendTimer}s`}
                                </button>
                            }
                        </div>
                    </Card>
                </Row>
            )}
        </Animate>
    )
}